import {
  dispatchAction,
  reducedStream,
  reducer,
  streamReducer,
} from '@ardoq/rxbeach';
import {
  requestFetchOptionsInstanceCounts,
  responseInstanceCounts,
  setSectionFilter,
  responseOptionsInstanceCounts,
  setOptionCountLoadingState,
  setStartTypeAndTraversalStartSetOrCount,
  setTripleSortOrder,
  toggleIsSelectedGraphNode,
  toggleIsSelectedGraphEdge,
  toggleShowOnlyOptionsWithInstanceCounts,
  toggleTripleOption,
  setTraversals,
  changeWildcardTraversal,
  setPathMatchingType,
  updateTraversalStartContext,
  invalidateInstanceCounts,
  setShouldIncludeInstanceCounts,
  setStartSetExpansion,
  removeNodeFromGraph,
  resetTraversalBuilderState,
  dontShowAgainClickOtherComponentTypeNodesHints,
  expandDefaultTraversalRequested,
} from './editTraversalActions';
import { editTraversalOperations } from './editTraversalOperations';
import {
  addDefaultFilter,
  changeFilterCondition,
  deleteFilter,
  updateFilter,
} from '../addFiltersTab/viewpointBuilderFilterActions';
import { map, shareReplay, tap, withLatestFrom, type Observable } from 'rxjs';
import type {
  TraversalBuilderState,
  TraversalBuilderViewState,
} from '../types';
import { viewpointBuilderFiltersOperations } from './viewpointBuilderFiltersOperations';
import { fields$ } from '../../streams/fields/fields$';
import { metamodelViewOperations } from '../metaModel/metamodelViewOperations';
import {
  getMissingHierarchyTypes,
  traversalEditStateToTraversalViewState,
} from './traversalEditStateToTraversalViewState';
import { dishonestCurrentUser$ as currentUser$ } from 'streams/currentUser/currentUser$';
import { persistentWorkspaces$ as workspaces$ } from 'streams/workspaces/workspaces$';
import { collapsePathsReducers } from '../collapsePathsTab/collapsePathsReducers';
import { setStateProperty } from '@ardoq/common-helpers';
import { requiredComponentsReducers } from '../requiredComponentsTab/requiredComponentsReducers';
import { loadMetaModel$ } from 'architectureModel/loadMetaModel';
import { logError } from '@ardoq/logging';
import { locale$ } from 'streams/locale$';

export const createEditTraversals$ = (
  namespace: string
): Observable<TraversalBuilderViewState> =>
  reducedStream<TraversalBuilderState>(
    `editTraversals$:${namespace}`,
    editTraversalOperations.getInitialTraversalEditState(),
    [
      streamReducer(loadMetaModel$, editTraversalOperations.processMetaModel),
      streamReducer(
        currentUser$,
        editTraversalOperations.processCurrentUserSettings
      ),
      streamReducer(fields$, metamodelViewOperations.createFieldMap),
      streamReducer(locale$, setStateProperty('locale')),
      // Resets the selected graph
      reducer(
        setStartTypeAndTraversalStartSetOrCount,
        editTraversalOperations.setStartTypeAndTraversalStartSetOrCount
      ),
      reducer(toggleTripleOption, editTraversalOperations.toggleTripleOption),
      reducer(removeNodeFromGraph, editTraversalOperations.removeNodeFromGraph),
      reducer(
        toggleIsSelectedGraphNode,
        editTraversalOperations.toggleIsSelectedGraphNodeAndUpdateTripleOptions
      ),
      reducer(
        toggleIsSelectedGraphEdge,
        editTraversalOperations.toggleIsSelectedGraphEdgeAndUpdateTripleOptions
      ),
      reducer(setTripleSortOrder, editTraversalOperations.setTripleSortOrder),
      reducer(
        responseInstanceCounts,
        editTraversalOperations.setInstanceCounts
      ),
      reducer(setSectionFilter, editTraversalOperations.setSectionFilter),
      reducer(
        addDefaultFilter,
        viewpointBuilderFiltersOperations.addDefaultFilter
      ),
      reducer(
        changeFilterCondition,
        viewpointBuilderFiltersOperations.changeFilterCondition
      ),
      reducer(updateFilter, viewpointBuilderFiltersOperations.updateFilter),
      reducer(deleteFilter, viewpointBuilderFiltersOperations.deleteFilter),
      reducer(
        responseOptionsInstanceCounts,
        editTraversalOperations.setOptionsInstanceCounts
      ),
      reducer(
        invalidateInstanceCounts,
        editTraversalOperations.invalidateInstanceCounts
      ),
      reducer(
        setOptionCountLoadingState,
        editTraversalOperations.setOptionCountLoadingState
      ),
      reducer(
        toggleShowOnlyOptionsWithInstanceCounts,
        editTraversalOperations.toggleShowOnlyOptionsWithInstanceCounts
      ),
      reducer(setTraversals, editTraversalOperations.setTraversal),
      reducer(
        changeWildcardTraversal,
        editTraversalOperations.changeWildcardTraversal
      ),
      reducer(
        dontShowAgainClickOtherComponentTypeNodesHints,
        editTraversalOperations.dontShowAgainClickOtherComponentTypeNodesHints
      ),
      reducer(
        updateTraversalStartContext,
        editTraversalOperations.updateTraversalStartContext
      ),
      reducer(setPathMatchingType, editTraversalOperations.setPathMatchingType),
      reducer(
        setShouldIncludeInstanceCounts,
        setStateProperty('shouldIncludeInstanceCounts')
      ),
      reducer(
        setStartSetExpansion,
        editTraversalOperations.applyStartSetExpansion
      ),
      reducer(
        resetTraversalBuilderState,
        editTraversalOperations.resetTraversalBuilderState
      ),
      reducer(
        expandDefaultTraversalRequested,
        editTraversalOperations.expandDefaultTraversal
      ),
      ...collapsePathsReducers,
      ...requiredComponentsReducers,
    ],
    { namespace }
  ).pipe(
    tap(({ tripleOptions: { optionCountLoadingState, fetchCountsArgs } }) => {
      if (optionCountLoadingState === 'idle' && fetchCountsArgs) {
        dispatchAction(
          requestFetchOptionsInstanceCounts(fetchCountsArgs),
          namespace
        );
        setTimeout(() =>
          dispatchAction(setOptionCountLoadingState('loading'), namespace)
        );
      }
    }),
    tap(state => {
      const missingTypes = getMissingHierarchyTypes(state);
      if (missingTypes.length > 0) {
        logError(
          Error(
            'Component type in the hierarchy is missing from the metamodel'
          ),
          null,
          { missingTypes: missingTypes.join(', ') }
        );
      }
    }),
    withLatestFrom(currentUser$, workspaces$),
    map(([state, currentUser, workspaces]) =>
      traversalEditStateToTraversalViewState(
        state,
        workspaces.byId,
        currentUser.organization?._id ?? ''
      )
    ),
    shareReplay({ bufferSize: 1, refCount: true })
  );
